import { FC, useState, useEffect, useRef } from "react";
import {
  StyledProgressBarContainer,
  StyledLabel,
  StyledValueLabel,
  StyledProgressTrack,
  StyledProgressFill,
} from "./progress-bar.style";
import { MDSProgressBarProps } from "./progress-bar.types";

export const MDSProgressBar: FC<MDSProgressBarProps> = ({
  label,
  showValueLabel,
  size = "medium",
  color,
  direction = "ltr",
  animation,
  rounded,
  value,
  minValue = 0,
  maxValue = 100,
  children,
  ...props
}) => {
  const { progressValue, isAnimating } = useAnimated({
    minValue,
    maxValue,
    value,
    animation,
  });

  return (
    <StyledProgressBarContainer
      direction={direction}
      animation={animation}
      value={progressValue}
      minValue={minValue}
      maxValue={maxValue}
      {...props}
    >
      {({ percentage, valueText, isIndeterminate }) => (
        <>
          {label && <StyledLabel>{label}</StyledLabel>}
          {showValueLabel && !isIndeterminate && <StyledValueLabel>{valueText}</StyledValueLabel>}
          <StyledProgressTrack size={size} direction={direction} rounded={rounded} $color={color}>
            <StyledProgressFill
              className="progress-fill"
              color={color}
              direction={direction}
              percentage={getPercentage(percentage, isIndeterminate)}
              isAnimating={isAnimating}
              rounded={rounded}
            />
          </StyledProgressTrack>
          {children}
        </>
      )}
    </StyledProgressBarContainer>
  );
};

const getPercentage = (percentage: number | undefined, isIndeterminate: boolean) => {
  if (isIndeterminate) {
    return 0;
  }

  return percentage ?? 0;
};

// Linear interpolation function
const lerp = (start: number, end: number, t: number): number => {
  return start + (end - start) * t;
};

// Initial value based on animation direction
const getInitialValue = (
  animationDirection: "increase" | "decrease",
  minValue: number,
  maxValue: number
) => {
  return animationDirection === "increase" ? minValue : maxValue;
};

const useAnimated = ({
  minValue = 0,
  maxValue = 100,
  value: controlledValue,
  animation,
}: MDSProgressBarProps) => {
  const {
    duration = 5000,
    paused = false,
    direction: animationDirection = "increase",
    onComplete: onAnimationComplete,
  } = animation ?? {};

  const [isAnimating, setIsAnimating] = useState(false);
  const animationRef = useRef<number>();
  const startTimeRef = useRef<number>();
  const totalElapsedRef = useRef<number>(0);
  const [animatedValue, setAnimatedValue] = useState(
    getInitialValue(animationDirection, minValue, maxValue)
  );

  // Animation effect when duration is provided
  useEffect(() => {
    // Early exit if animation conditions aren't met
    if (!duration || duration <= 0 || controlledValue !== undefined) {
      return;
    }

    // Early exit if paused - handle pause logic
    if (paused) {
      // Early exit if no animation is running
      if (!animationRef.current) {
        setIsAnimating(false);
        startTimeRef.current = undefined;
        return;
      }

      // Update elapsed time if we have a start time
      if (startTimeRef.current) {
        totalElapsedRef.current += performance.now() - startTimeRef.current;
      }

      // Cancel the current animation
      cancelAnimationFrame(animationRef.current);
      setIsAnimating(false);
      startTimeRef.current = undefined;

      return;
    }

    // Main animation logic - no longer nested
    setIsAnimating(true);

    // Determine start and end values based on animationDirection
    const startValue = animationDirection === "increase" ? minValue : maxValue;
    const endValue = animationDirection === "increase" ? maxValue : minValue;

    const animate = (currentTime: number) => {
      if (!startTimeRef.current) {
        startTimeRef.current = currentTime;
      }

      const elapsed = totalElapsedRef.current + (currentTime - startTimeRef.current);
      const progress = Math.min(elapsed / duration, 1);

      // Use easing function for smoother animation (ease-in-out)
      const easedProgress = progress * (2 - progress);
      const currentValue = lerp(startValue, endValue, easedProgress);

      setAnimatedValue(currentValue);

      if (progress < 1) {
        animationRef.current = requestAnimationFrame(animate);
      } else {
        setIsAnimating(false);
        totalElapsedRef.current = 0; // Reset for next animation
        onAnimationComplete?.();
      }
    };

    // Reset animation if starting fresh
    if (totalElapsedRef.current === 0) {
      setAnimatedValue(startValue);
    }

    animationRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }

      setIsAnimating(false);
    };
  }, [
    duration,
    animationDirection,
    minValue,
    maxValue,
    controlledValue,
    paused,
    onAnimationComplete,
  ]);

  // Use controlled value if provided, otherwise use animated value when duration is set
  const progressValue =
    controlledValue !== undefined ? controlledValue : duration ? animatedValue : undefined;

  return { progressValue, isAnimating };
};
